// Cámara dentro de la página: no sale del sistema ni depende de la app de cámara de Android ni de su almacenamiento,
// y si falla el sistema sí se entera y puede explicar por qué. La foto queda en memoria hasta subirse.
// La app de cámara del dispositivo queda como alternativa: si falla ahí, Android muestra su propio aviso.
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Swal from 'sweetalert2';
import FondoModal from '../common/FondoModal';

const MAX_LADO = 1600;
const SIGUE = 'Lo que escribiste no se pierde: puedes guardar sin foto y tomarla antes de cerrar caja.';
const motivoCamara = (e) => {
  if (['NotAllowedError', 'SecurityError'].includes(e?.name)) return 'El permiso de la cámara está bloqueado. Actívalo en los permisos de este sitio en el navegador.';
  if (['NotFoundError', 'OverconstrainedError'].includes(e?.name)) return 'No se encontró una cámara en este dispositivo.';
  if (['NotReadableError', 'AbortError'].includes(e?.name)) return 'La cámara está en uso por otra aplicación o no responde. Ciérrala e intenta de nuevo.';
  return 'No se pudo abrir la cámara.';
};
const apagar = (flujo) => flujo.getTracks().forEach((t) => t.stop());

function Visor({ onFoto, onCerrar, onDispositivo }) {
  const video = useRef(null);
  const [lista, setLista] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let vivo = true;
    let flujo = null;
    navigator.mediaDevices.getUserMedia({ audio: false, video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } } })
      .then((s) => { if (!vivo) { apagar(s); return; } flujo = s; video.current.srcObject = s; })
      .catch((e) => { if (vivo) setError(motivoCamara(e)); });
    return () => { vivo = false; if (flujo) apagar(flujo); };
  }, []);

  const capturar = () => {
    const fallo = () => setError('No se pudo capturar la foto. Si el dispositivo tiene poca memoria, cierra otras aplicaciones e intenta de nuevo.');
    try {
      const v = video.current;
      const k = Math.min(1, MAX_LADO / Math.max(v.videoWidth, v.videoHeight));
      const c = document.createElement('canvas');
      c.width = Math.round(v.videoWidth * k); c.height = Math.round(v.videoHeight * k);
      c.getContext('2d').drawImage(v, 0, 0, c.width, c.height);
      c.toBlob((b) => (b ? onFoto(new File([b], `foto-${Date.now()}.jpg`, { type: 'image/jpeg' })) : fallo()), 'image/jpeg', 0.85);
    } catch { fallo(); }
  };

  return (
    <FondoModal onClose={onCerrar} className="fx-backdrop fx-camara-fondo" confirmar={false}>
      <div className="fx-camara" role="dialog" aria-label="Tomar foto">
        {error ? <div className="fx-camara-aviso" role="alert">
          <p><b>{error}</b></p>
          <p>{SIGUE}</p>
          <small>También puedes usar la cámara del dispositivo. Si Android avisa que el almacenamiento está lleno, hay que liberar espacio en el dispositivo.</small>
        </div> : <video ref={video} className="fx-camara-video" autoPlay muted playsInline onLoadedData={() => setLista(true)} />}
        <div className="fx-camara-barra">
          <button type="button" className="fx-btn" onClick={onCerrar}>Cerrar</button>
          {error ? <button type="button" className="fx-btn fx-btn--primary" onClick={onDispositivo}>Usar cámara del dispositivo</button>
            : <button type="button" className="fx-btn fx-btn--primary" disabled={!lista} onClick={capturar}>{lista ? 'Capturar' : 'Abriendo cámara…'}</button>}
        </div>
      </div>
    </FondoModal>
  );
}

/** `abrir` inicia la cámara y `vista` se coloca en la pantalla; `alTomar` recibe el File de la foto. */
export function useTomarFoto(alTomar) {
  const entrada = useRef(null);
  const [abierta, setAbierta] = useState(false);

  const abrir = () => { if (navigator.mediaDevices?.getUserMedia) setAbierta(true); else entrada.current?.click(); };
  const delDispositivo = (e) => {
    const f = e.target.files?.[0];
    e.target.value = ''; // permite volver a elegir el mismo archivo
    if (!f) return;
    if (!f.size) { Swal.fire('Cámara', `La cámara no entregó la foto. Revisa que el dispositivo tenga espacio disponible e intenta de nuevo. ${SIGUE}`, 'warning'); return; }
    alTomar(f);
  };

  const vista = <>
    <input ref={entrada} type="file" accept="image/*" capture="environment" hidden onChange={delDispositivo} />
    {abierta && createPortal(<Visor onFoto={(f) => { setAbierta(false); alTomar(f); }} onCerrar={() => setAbierta(false)}
      onDispositivo={() => { setAbierta(false); entrada.current?.click(); }} />, document.body)}
  </>;
  return { abrir, vista };
}
